import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import dayjs from 'dayjs';
import {useMutation} from '@tanstack/react-query';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import {Button, cn, useModalControls} from '@chekin/guestapp-ui-kit';
import ChekinProSettings from 'ChekinProSettings';
import {
  ALL_GROUP_TYPES_OPTIONS,
  GROUP_TYPES,
  MaxNumberOfGuests,
  MinNumberOfGuests,
  OPTION_VALUES,
  formatOneDate,
  getMinCheckInDate,
  isSameOrAfterDay,
  validateCheckOut,
} from 'pages/SearchReservation/utils';
import {
  DATE_FORMAT,
  SelectOption,
  ShortReservation,
  LocalStorage,
  parseDate,
  PATTERNS,
  useStatus,
  mergeRefs,
  COUNTRY_CODES,
  useTimeoutRef,
} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {RESERVATION_ID_KEY, STATUSES} from 'utils/constants';
import {usePath} from 'hooks';
import {useVirtualKeyboard} from 'hooks/useVirtualKeyboard';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useHousing} from '../../useHousing';
import {API, ReservationPayload} from '@chekin/api';
import {StatusModalStates, useErrorModal} from '@chekin/shared';
import {createSession} from 'components/sessions/utils';
import PageUniversalLink from 'components/PageUniversalLink';
import Datepicker, {DatepickerHandle} from 'components/common/Datepicker';
import {Select, InputController as Input} from '@guestapp/old-ui-kit';
import {ViewMainInfo} from '../../components/ViewMainInfo';
import {SearchingBookingModal} from '../../components/SearchingBookingModal';
import {FormFieldWrapper} from 'styled/common';
import {StyledBackIcon} from '../CheckInDateView/styled';
import {ContentButton, CustomBackButton, Fields, FormWrapper, Wrapper} from './styled';

enum FORM_NAMES {
  lead_guest_name = 'default_leader_full_name',
  lead_email = 'default_invite_email',
  number_of_guests = 'number_of_guests',
  type_of_registration = 'type',
  check_in_date = 'check_in_date',
  check_out_date = 'check_out_date',
}

const buildPayload = (data: FormTypes, housingIdPropertyLink: string) => {
  const {number_of_guests, check_out_date, check_in_date, type, ...rest} = data;

  return {
    ...rest,
    [FORM_NAMES.check_in_date]: check_in_date
      ? dayjs(parseDate(check_in_date)).format(DATE_FORMAT)
      : undefined,
    [FORM_NAMES.check_out_date]: check_out_date
      ? dayjs(parseDate(check_out_date)).format(DATE_FORMAT)
      : undefined,
    guest_group: {
      number_of_guests,
      ...(type && {type: type.value}),
    },
    housing_id: housingIdPropertyLink,
    source_name: 'Property Link',
  };
};

type FormTypes = {
  [FORM_NAMES.lead_guest_name]: string;
  [FORM_NAMES.number_of_guests]: string | number;
  [FORM_NAMES.lead_email]?: string;
  [FORM_NAMES.type_of_registration]?: {label: string; value: string};
  [FORM_NAMES.check_in_date]?: Date;
  [FORM_NAMES.check_out_date]?: Date;
};

function NewReservationView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const location = useLocation();
  const {isLoading, setStatus} = useStatus();
  const isLocationDataPreloaded = useRef(false);
  const {ErrorModal, displayError} = useErrorModal();
  const timeoutRef = useTimeoutRef();
  const {housing, housingCountry, isLoaded} = useHousing();
  const [groupTypes, setGroupTypes] = useState<SelectOption[]>(ALL_GROUP_TYPES_OPTIONS());

  const [modalRegisterGuestStatus, setModalRegisterGuestStatus] = useState(
    StatusModalStates.IDLE,
  );
  const layoutMode = useLayoutMode();

  const checkInDatepickerRef = useRef<DatepickerHandle>(null);
  const checkOutDatepickerRef = useRef<DatepickerHandle>(null);

  const {
    isOpen: isOpenReservationStatusModal,
    openModal: openReservationStatusModal,
    closeModal: closeReservationStatusModal,
  } = useModalControls();

  const {
    setValue,
    getValues,
    register,
    formState: {errors, isSubmitted},
    trigger,
    watch,
    control,
    handleSubmit,
  } = useForm<FormTypes>();

  const isTypeShown =
    housingCountry === COUNTRY_CODES.austria ||
    housingCountry === COUNTRY_CODES.uae ||
    housingCountry === COUNTRY_CODES.italy;

  const numberOfGuests = watch(FORM_NAMES.number_of_guests);
  const typeOfRegistration = watch(FORM_NAMES.type_of_registration);

  const {mutate} = useMutation<ShortReservation, Error, ReservationPayload>({
    mutationFn: payload => API.reservation.createReservation(payload),
    onSuccess: data => {
      const reservationId = data.id;
      const reservationSignUpFormLink = data?.signup_form_link;

      if (reservationId) {
        if (ChekinProSettings.isSDK) {
          setStatus(STATUSES.success);
          setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
          ChekinProSettings.events.onReservationCreated({id: reservationId});
          return;
        }
        setStatus(STATUSES.success);

        createSession({reservationId});

        LocalStorage.set(RESERVATION_ID_KEY, reservationId);
        window.location.href = reservationSignUpFormLink;
        setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
      }
    },
    onError: error => {
      setModalRegisterGuestStatus(StatusModalStates.IDLE);
      closeReservationStatusModal();
      setStatus(STATUSES.error);
      displayError(error);
    },
  });

  const onSubmit = (formData: FormTypes) => {
    setStatus(STATUSES.loading);
    setModalRegisterGuestStatus(StatusModalStates.LOADING);
    openReservationStatusModal();
    const payload = buildPayload(formData, housing?.id ?? '');
    mutate(payload);
  };

  const validateCheckInDate = useCallback(
    (value?: string | Date) => {
      if (!value) return value;

      const minCheckInDate = getMinCheckInDate(housingCountry);
      const isAfterMinCheckInDate = isSameOrAfterDay(value, minCheckInDate);
      if (isAfterMinCheckInDate) return true;

      const formatMinCheckInDate = formatOneDate({
        date: minCheckInDate,
        housingCountry: housingCountry ?? '',
      });
      return t(`minimum_check_in_date_is`, {date: formatMinCheckInDate});
    },
    [housingCountry, t],
  );

  const validateCheckOutDate = (value?: string | Date) => {
    if (!value) return value;
    const checkInDate = getValues()[FORM_NAMES.check_in_date];
    return validateCheckOut(value, checkInDate);
  };

  useEffect(
    function preloadKnowsFields() {
      const locationState = location?.state;
      if (isLocationDataPreloaded.current || !locationState) return;

      const preload = [
        {
          name: FORM_NAMES.check_in_date,
          value: locationState.startDate,
        },
        {
          name: FORM_NAMES.check_out_date,
          value: locationState.endDate,
        },
        {
          name: FORM_NAMES.lead_email,
          value: locationState.email,
        },
      ].filter(f => !!f.value);

      preload.forEach(field => {
        setValue(field.name, field.value);
      });
      isLocationDataPreloaded.current = true;
    },
    [location, setValue],
  );

  useEffect(
    function updateGroupTypesOnNumberOfGuestsChange() {
      const tourist_group_type_option = {
        value: GROUP_TYPES.tourist,
        label: t('tourist_group'),
      };
      const single_group_type_option = {
        value: GROUP_TYPES.single,
        label: t('single'),
      };
      const GROUP_GROUP_TYPES_OPTIONS = [
        {
          value: GROUP_TYPES.group,
          label: t('group'),
        },
        {
          value: GROUP_TYPES.family,
          label: t('family'),
        },
      ];
      const defaultAllGroupTypes = ALL_GROUP_TYPES_OPTIONS();
      if (housingCountry === COUNTRY_CODES.austria) {
        defaultAllGroupTypes.push(tourist_group_type_option);
      }
      if (numberOfGuests === undefined || numberOfGuests === '') {
        setGroupTypes(defaultAllGroupTypes);
        return;
      }

      let totalGuests = Number(numberOfGuests);
      totalGuests = totalGuests < 0 ? 0 : totalGuests;

      if (totalGuests > 1) {
        if (housingCountry === COUNTRY_CODES.austria) {
          setGroupTypes([...GROUP_GROUP_TYPES_OPTIONS, tourist_group_type_option]);
        } else {
          setGroupTypes(GROUP_GROUP_TYPES_OPTIONS);
        }
        return;
      }

      if (totalGuests > 0) {
        setGroupTypes([single_group_type_option]);
      }
    },
    [numberOfGuests, housingCountry, t],
  );

  useEffect(
    function keepCorrectRegistrationType() {
      if (!typeOfRegistration || !groupTypes.length) {
        return;
      }

      const hasSelectedGroupTypeOption = groupTypes.find(
        g => g?.value === typeOfRegistration?.value,
      );
      if (!hasSelectedGroupTypeOption) {
        setValue(FORM_NAMES.type_of_registration, undefined);
      }
    },
    [typeOfRegistration, groupTypes, setValue, trigger],
  );

  useEffect(
    function changeTranslationErrorMessages() {
      if (isSubmitted) {
        void trigger();
      }
    },
    [isSubmitted, trigger],
  );

  useEffect(() => {
    if (
      !ChekinProSettings.isSDK &&
      modalRegisterGuestStatus === StatusModalStates.SUCCESS
    ) {
      timeoutRef.current = setTimeout(() => {
        closeReservationStatusModal();
      }, 3000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [closeReservationStatusModal, modalRegisterGuestStatus, timeoutRef]);

  const virtualKeyboardConfig = useMemo(
    () => ({
      fields: {
        [FORM_NAMES.number_of_guests]: {
          getValue: () => getValues(FORM_NAMES.number_of_guests)?.toString() || '',
          setValue: (value: string) => {
            setValue(FORM_NAMES.number_of_guests, value === '' ? '' : Number(value));
          },
        },
        [FORM_NAMES.lead_guest_name]: {
          getValue: () => getValues(FORM_NAMES.lead_guest_name) || '',
          setValue: (value: string) => {
            setValue(FORM_NAMES.lead_guest_name, value);
          },
        },
        [FORM_NAMES.lead_email]: {
          getValue: () => getValues(FORM_NAMES.lead_email) || '',
          setValue: (value: string) => {
            setValue(FORM_NAMES.lead_email, value);
          },
        },
        check_in_date_day: {
          getValue: () => checkInDatepickerRef.current?.getDayValue() || '',
          setValue: (value: string) => {
            checkInDatepickerRef.current?.setDayValue(value);
          },
        },
        check_in_date_year: {
          getValue: () => checkInDatepickerRef.current?.getYearValue() || '',
          setValue: (value: string) => {
            checkInDatepickerRef.current?.setYearValue(value);
          },
        },
        check_out_date_day: {
          getValue: () => checkOutDatepickerRef.current?.getDayValue() || '',
          setValue: (value: string) => {
            checkOutDatepickerRef.current?.setDayValue(value);
          },
        },
        check_out_date_year: {
          getValue: () => checkOutDatepickerRef.current?.getYearValue() || '',
          setValue: (value: string) => {
            checkOutDatepickerRef.current?.setYearValue(value);
          },
        },
      },
    }),
    [getValues, setValue],
  );

  const {handleFocus, handleBlur, keyboard, isVisible} =
    useVirtualKeyboard(virtualKeyboardConfig);

  const handleBackClick = () => {
    const path =
      location.state?.from === OPTION_VALUES.booking_reference
        ? PATHS.searchReservation.bookingCode
        : PATHS.searchReservation.checkingDate;
    navigate(getFullPath(path), {state: {...location?.state}});
  };

  return (
    <PageUniversalLink className="new-reservation-view">
      <Wrapper className="kiosko:mt-[260px] kiosko:gap-10" $isKeyboardVisible={isVisible}>
        <ViewMainInfo
          title={t('find_your_booking')}
          subTitle={t('enter_the_check_in_date_and_the_lead_guest_email')}
        />

        {layoutMode === 'default' && (
          <CustomBackButton
            label={t('back')}
            customIcon={<StyledBackIcon name="backArrow" size={15} />}
            iconProps={{position: 'left'}}
            onClick={handleBackClick}
            width="auto"
            className="summary-view__back-btn"
            link
          />
        )}

        <FormWrapper>
          <Fields
            className={cn(
              'guest-form-fields',
              'kiosko:w-full kiosko:max-w-[none] kiosko:grid kiosko:grid-cols-1 kiosko:gap-8',
            )}
            $rowsCount={1}
          >
            <FormFieldWrapper className="kiosko:w-full kiosko:mb-[68px]">
              <Controller
                name={FORM_NAMES.check_in_date}
                rules={{required: t('required'), validate: validateCheckInDate}}
                control={control}
                render={({field: {ref: controllerRef, ...field}}) => {
                  return (
                    <Datepicker
                      ref={mergeRefs(checkInDatepickerRef, controllerRef)}
                      error={errors[FORM_NAMES.check_in_date]?.message}
                      label={t('check_in_date')}
                      onFocus={() => {
                        handleFocus(FORM_NAMES.check_in_date);
                      }}
                      onFieldFocus={subfield => {
                        handleFocus(`check_in_date_${subfield}`);
                      }}
                      {...field}
                    />
                  );
                }}
              />
            </FormFieldWrapper>

            <FormFieldWrapper className="kiosko:w-full kiosko:mb-[68px]">
              <Controller
                name={FORM_NAMES.check_out_date}
                rules={{required: t('required'), validate: validateCheckOutDate}}
                control={control}
                render={({field: {ref: controllerRef, ...field}}) => {
                  return (
                    <Datepicker
                      ref={mergeRefs(checkOutDatepickerRef, controllerRef)}
                      error={errors[FORM_NAMES.check_out_date]?.message}
                      label={t('check_out_date')}
                      onFocus={() => {
                        handleFocus(FORM_NAMES.check_out_date);
                      }}
                      onFieldFocus={subfield => {
                        handleFocus(`check_out_date_${subfield}`);
                      }}
                      {...field}
                    />
                  );
                }}
              />
            </FormFieldWrapper>

            <FormFieldWrapper className="kiosko:w-full kiosko:mb-[68px]">
              <Input
                {...register(FORM_NAMES.number_of_guests, {
                  required: t('required'),
                  min: {
                    value: MinNumberOfGuests,
                    message: t('min_number_is_short', {number: MinNumberOfGuests}),
                  },
                  max: {
                    value: MaxNumberOfGuests,
                    message: t('max_number_is_short', {number: MaxNumberOfGuests}),
                  },
                })}
                type="number"
                label={t('total_number_of_guests')}
                control={control}
                error={errors[FORM_NAMES.number_of_guests]?.message}
                inputMode="numeric"
                autoCorrect="off"
                spellCheck={false}
                autoCapitalize="none"
                onFocus={() => {
                  handleFocus(FORM_NAMES.number_of_guests);
                }}
                onBlur={() => {
                  handleBlur(FORM_NAMES.number_of_guests);
                }}
              />
            </FormFieldWrapper>

            <FormFieldWrapper className="kiosko:w-full kiosko:mb-[68px]">
              <Input
                {...register(FORM_NAMES.lead_guest_name, {
                  required: t('required'),
                })}
                label={t('lead_guest_name')}
                control={control}
                error={errors[FORM_NAMES.lead_guest_name]?.message}
                inputMode="email"
                autoCorrect="off"
                spellCheck={false}
                autoCapitalize="none"
                onFocus={() => {
                  handleFocus(FORM_NAMES.lead_guest_name);
                }}
                onBlur={() => {
                  handleBlur(FORM_NAMES.lead_guest_name);
                }}
              />
            </FormFieldWrapper>

            {isTypeShown && (
              <FormFieldWrapper className="kiosko:w-full kiosko:mb-[68px]">
                <Controller
                  name={FORM_NAMES.type_of_registration}
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <Select
                      options={groupTypes}
                      label={t('type_of_registration')}
                      disabled={isLoading}
                      error={error?.message}
                      {...field}
                    />
                  )}
                  rules={{required: `${t('required')}`}}
                />
              </FormFieldWrapper>
            )}

            <FormFieldWrapper className="kiosko:w-full kiosko:mb-0">
              <Input
                {...register(FORM_NAMES.lead_email, {
                  required: t('required'),
                  pattern: {
                    value: PATTERNS.email,
                    message: t('invalid_email'),
                  },
                })}
                type="email"
                label={t('lead_guest_email')}
                control={control}
                error={errors[FORM_NAMES.lead_email]?.message}
                inputMode="email"
                autoCorrect="off"
                spellCheck={false}
                autoCapitalize="none"
                onFocus={() => {
                  handleFocus(FORM_NAMES.lead_email);
                }}
                onBlur={() => {
                  handleBlur(FORM_NAMES.lead_email);
                }}
              />
            </FormFieldWrapper>
          </Fields>
        </FormWrapper>

        <ContentButton>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || !isLoaded}
            className={cn(
              'submit-btn',
              'min-w-[205px]',
              'kiosko:mx-auto kiosko:mt-4 kiosko:block',
            )}
            variant="default"
          >
            {t('confirm')}
          </Button>
        </ContentButton>
      </Wrapper>
      <SearchingBookingModal
        isOpen={isOpenReservationStatusModal}
        status={modalRegisterGuestStatus}
      />
      <ErrorModal />
      {keyboard}
    </PageUniversalLink>
  );
}

export {NewReservationView};
